/**
 * Settlement API client.
 *
 * Maintenance (super-admin) endpoints: api/maintenance/settlement/...
 * Org-facing (tenant-isolated) endpoints: api/analytics/tenants/{tenantId}/settlement/...
 *
 * v1: runs are PER OPERATING ORG, PER CALENDAR QUARTER (Jan start; period_key 'YYYY-QN').
 * Only completed quarters can be settled; once settled a run is immutable. All money in
 * INR; per-kWh figures are pre-GST taxable. See docs/settlement-design.md in admin-api.
 */
import axiosInstance from "../axiosInstance";

// ---- shared enums -----------------------------------------------------------
export type PartyType = "platform" | "host" | "consultant" | "custom";
export type GstStatus = "regular" | "composition" | "unregistered" | "none";
export type LineKind = "share" | "consumable";
export type GstTreatment = "exempt" | "gst_18_forward" | "gst_18_rcm";
export type RunStatus = "none" | "draft" | "settled";

// ---- types ------------------------------------------------------------------
export interface OrgSummary {
  org_id: number;
  org_name: string;
  org_gstin: string | null;
  priced_configs: number;
  configured: number;
}

export interface CoverageItem {
  pricing_override_id: number;
  org_id: number;
  org_name: string;
  charging_station_id: number;
  station_name: string;
  charger_type: string;
  price_per_kwh: number;
  is_default: boolean;
  priority: number;
  valid_from: string;
  valid_thru: string;
  service_type_key: string | null;
  price_org_name: string | null; // consuming org for this price tier
}
export interface CoverageResponse {
  total_priced: number;
  configured: number;
  missing: number;
  coverage_pct: number;
  items: CoverageItem[];
}

export interface OrgSetupStation {
  pricing_override_id: number;
  charging_station_id: number;
  station_name: string;
  charger_type: string;
  price_per_kwh: number;
  is_default: boolean;
  priority: number;
  price_org_name: string | null; // consuming org for this price tier
  service_type_key: string | null;
  tax_model: string | null; // inclusive | exclusive
  gst_pct: number | null;
  suggested_taxable: number | null; // de-grossed taxable base the rate card sums to
  profile_id: number | null;
  taxable_unit_price: number | null;
  line_count: number;
  configured: boolean;
}
export interface OrgSetupResponse {
  org_id: number;
  org_name: string;
  org_gstin: string | null;
  priced_configs: number;
  configured: number;
  missing: number;
  stations: OrgSetupStation[];
}

export interface Party {
  id: number;
  party_guid: string;
  name: string;
  party_type: PartyType;
  org_id: number | null; // null = global/shared; else org-level (that org's party)
  org_name: string | null; // name of the org this party belongs to (org-level only)
  gst_registration_status: GstStatus;
  gstin: string | null;
  default_sac: string | null;
  is_active: boolean;
  used_in: number;
}
export interface PartyInput {
  name: string;
  party_type: PartyType;
  org_id?: number | null;
  gst_registration_status?: GstStatus;
  gstin?: string | null;
  default_sac?: string | null;
  payout_meta?: Record<string, unknown> | null;
}

export interface ProfileLineInput {
  line_kind: LineKind;
  party_id: number;
  label: string;
  rate_per_kwh: number;
  gst_treatment?: GstTreatment;
  sort_order?: number;
}
export interface ProfileLine extends ProfileLineInput {
  id: number;
  party_name: string | null;
}
export interface Profile {
  id: number;
  profile_guid: string;
  pricing_override_id: number;
  org_id: number;
  org_name: string | null;
  charging_station_id: number;
  station_name: string | null;
  charger_type: string;
  taxable_unit_price: number;
  balancer_party_id: number | null;
  status: string;
  lines: ProfileLine[];
}
export interface ProfileInput {
  pricing_override_id: number;
  taxable_unit_price: number;
  balancer_party_id?: number | null;
  lines: ProfileLineInput[];
}
export interface ProfileUpdate {
  taxable_unit_price: number;
  balancer_party_id?: number | null;
  lines: ProfileLineInput[];
}

// ---- run (per org, per calendar quarter) -----------------------------------
export interface RunPartyRollup {
  party_id: number;
  party_name: string;
  party_type: string;
  reimbursement: number;
  share: number;
  gst: number;
  total_payable: number;
  paid: number;
  fully_paid: boolean;
}
export interface RunProfileLine {
  line_kind: LineKind;
  label: string;
  party_id: number;
  party_name: string | null;
  rate_per_kwh: number;
  amount: number;
  gst_treatment: GstTreatment;
  gst_amount: number;
}
export interface RunProfileBreakdown {
  profile_id: number | null;
  station_name: string | null;
  charger_type: string | null;
  price_org_name: string | null;
  taxable_unit_price: number | null;
  kwh: number;
  lines: RunProfileLine[];
}
export interface RunSummary {
  org_id: number;
  org_name: string | null;
  period_key: string;
  period_start: string;
  period_end: string;
  cadence: string; // monthly | quarterly
  status: string; // draft | settled
  computed_by: string | null;
  settled_at: string | null;
  settled_by: string | null;
  parties: RunPartyRollup[];
  profiles: RunProfileBreakdown[];
  gst_ledger: { output_gst: number; input_credit: number; net_payable: number };
  uncovered: { kwh: number; sessions: number };
}
export type Cadence = "monthly" | "quarterly";
export interface OrgPeriod {
  period_key: string;
  period_start: string;
  period_end: string;
  year: number;
  period_num: number; // quarter 1..4 or month 1..12
  cadence: Cadence;
  sessions: number;
  kwh: number;
  status: RunStatus;
  settled_at: string | null;
  total_payable: number;
  completed: boolean; // period has ended
  can_settle: boolean; // completed and not settled
}
export interface OrgPeriodsResponse {
  org_id: number;
  org_name: string;
  cadence: Cadence;
  periods: OrgPeriod[];
}
export interface OrgSettings {
  org_id: number;
  cadence: Cadence;
}
export interface Payout {
  id: number;
  party_id: number;
  party_name: string;
  amount: number;
  reference: string | null;
  note: string | null;
  paid_by: string | null;
  paid_on: string;
}
export interface AuditEntry {
  action: string;
  actor: string | null;
  detail: Record<string, unknown> | null;
  at: string;
}

// ---- org-facing -------------------------------------------------------------
export interface OrgStationRow {
  station_id: number;
  station_name: string;
  kwh: number;
  reimbursement: number;
  share: number;
  total: number;
}
export interface OrgSettlementSummary {
  org_id: number;
  period_key: string;
  kwh: number;
  reimbursement: number;
  share: number;
  gst: number;
  total: number;
  stations: OrgStationRow[];
}
export interface OrgStatementRow {
  period_key: string;
  period_start: string;
  period_end: string;
  status: string;
  kwh: number;
  reimbursement: number;
  share: number;
  gst: number;
  total: number;
  paid: boolean;
}

// ---- maintenance (super-admin) ---------------------------------------------
const M = "api/maintenance/settlement";
const pk = (k: string) => encodeURIComponent(k);

export const listSettlementOrgs = () =>
  axiosInstance.get<OrgSummary[]>(`${M}/orgs`).then((r) => r.data);

export const getCoverage = () =>
  axiosInstance.get<CoverageResponse>(`${M}/coverage`).then((r) => r.data);

export const getOrgSetup = (orgId: number) =>
  axiosInstance.get<OrgSetupResponse>(`${M}/orgs/${orgId}/setup`).then((r) => r.data);

/** All parties, or — with orgId — global parties + that org's own parties. */
export const listParties = (orgId?: number) =>
  axiosInstance
    .get<Party[]>(`${M}/parties`, { params: orgId != null ? { org_id: orgId } : undefined })
    .then((r) => r.data);

export const createParty = (body: PartyInput) =>
  axiosInstance.post<Party>(`${M}/parties`, body).then((r) => r.data);

export const getProfile = (profileId: number) =>
  axiosInstance.get<Profile>(`${M}/profiles/${profileId}`).then((r) => r.data);

export const createProfile = (body: ProfileInput) =>
  axiosInstance.post<Profile>(`${M}/profiles`, body).then((r) => r.data);

export const updateProfile = (profileId: number, body: ProfileUpdate) =>
  axiosInstance.put<Profile>(`${M}/profiles/${profileId}`, body).then((r) => r.data);

export const cloneProfile = (sourceProfileId: number, targetPricingOverrideId: number) =>
  axiosInstance
    .post<Profile>(`${M}/profiles/clone`, {
      source_profile_id: sourceProfileId,
      target_pricing_override_id: targetPricingOverrideId,
    })
    .then((r) => r.data);

/** Periods (in the org's current cadence) with activity + each period's status. */
export const listOrgPeriods = (orgId: number) =>
  axiosInstance.get<OrgPeriodsResponse>(`${M}/orgs/${orgId}/periods`).then((r) => r.data);

/** The org's settlement cadence. */
export const getOrgSettings = (orgId: number) =>
  axiosInstance.get<OrgSettings>(`${M}/orgs/${orgId}/settings`).then((r) => r.data);

/** Set the org's cadence (monthly | quarterly). Applies going forward. */
export const setOrgCadence = (orgId: number, cadence: Cadence) =>
  axiosInstance.put<OrgSettings>(`${M}/orgs/${orgId}/settings`, { cadence }).then((r) => r.data);

/** Delete a DRAFT run (to re-slice under a changed cadence). Refuses settled runs. */
export const deleteDraftRun = (orgId: number, periodKey: string) =>
  axiosInstance.delete(`${M}/orgs/${orgId}/runs/${pk(periodKey)}`).then((r) => r.data);

/** Compute/recompute the draft run for an org + quarter (period 'YYYY-QN'). */
export const generateRun = (orgId: number, period?: string) =>
  axiosInstance
    .post<RunSummary>(`${M}/orgs/${orgId}/runs/generate`, null, {
      params: period ? { period } : undefined,
    })
    .then((r) => r.data);

export const getRun = (orgId: number, periodKey: string) =>
  axiosInstance.get<RunSummary>(`${M}/orgs/${orgId}/runs/${pk(periodKey)}`).then((r) => r.data);

/** Lock a run — draft → settled (immutable). Only completed quarters. */
export const settleRun = (orgId: number, periodKey: string) =>
  axiosInstance
    .post<RunSummary>(`${M}/orgs/${orgId}/runs/${pk(periodKey)}/settle`)
    .then((r) => r.data);

/** Record an append-only payout against a settled run. */
export const recordPayout = (
  orgId: number,
  periodKey: string,
  body: { party_id: number; amount: number; reference?: string; note?: string }
) =>
  axiosInstance
    .post<Payout>(`${M}/orgs/${orgId}/runs/${pk(periodKey)}/payouts`, body)
    .then((r) => r.data);

export const listPayouts = (orgId: number, periodKey: string) =>
  axiosInstance
    .get<Payout[]>(`${M}/orgs/${orgId}/runs/${pk(periodKey)}/payouts`)
    .then((r) => r.data);

export const getRunAudit = (orgId: number, periodKey: string) =>
  axiosInstance
    .get<AuditEntry[]>(`${M}/orgs/${orgId}/runs/${pk(periodKey)}/audit`)
    .then((r) => r.data);

// ---- org-facing (tenant-isolated) ------------------------------------------
const orgBase = (tenantId: string) => `api/analytics/tenants/${tenantId}/settlement`;

export const getOrgSettlementSummary = (tenantId: string, periodKey?: string) =>
  axiosInstance
    .get<OrgSettlementSummary>(`${orgBase(tenantId)}/summary`, {
      params: periodKey ? { period_key: periodKey } : undefined,
    })
    .then((r) => r.data);

export const getOrgSettlementStatements = (tenantId: string) =>
  axiosInstance.get<OrgStatementRow[]>(`${orgBase(tenantId)}/statements`).then((r) => r.data);

/** The settled quarters that cover an arbitrary date span (the '2–3 covering settlements'). */
export const getOrgSettlementCovering = (tenantId: string, from: string, to: string) =>
  axiosInstance
    .get<OrgStatementRow[]>(`${orgBase(tenantId)}/covering`, { params: { from, to } })
    .then((r) => r.data);
